import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Activity, Database, FileText, Wrench, BookOpen, CheckSquare, RefreshCw, Download, Play, CheckCircle2, XCircle, AlertTriangle, Clock, ChevronLeft, ChevronRight } from 'lucide-react';

const S = { OK: 'bg-green-500 text-white', WARNING: 'bg-amber-500 text-white', ERROR: 'bg-red-500 text-white' };
const LS = { DONE: 'bg-green-500 text-white', PENDING: 'bg-slate-400 text-white', BLOCKED: 'bg-red-500 text-white' };
const LL = { INFO: 'bg-blue-100 text-blue-700', WARNING: 'bg-amber-100 text-amber-700', ERROR: 'bg-red-100 text-red-700', CRITICAL: 'bg-red-200 text-red-800' };

function HealthTab() {
  const [health, setHealth] = useState(null);
  const [version, setVersion] = useState(null);
  const [errors, setErrors] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    const [h, v, e] = await Promise.all([
      api.get('/security/system/health').catch(() => null),
      api.get('/system/version').catch(() => null),
      api.get('/system/error-summary').catch(() => null),
    ]);
    if (h) setHealth(h.data.data);
    if (v) setVersion(v.data.data);
    if (e) setErrors(e.data.data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  if (loading) return <p className="text-muted-foreground py-4">Loading...</p>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {version && <Card><CardContent className="py-3"><p className="text-xs text-muted-foreground">Version</p><p className="font-bold">{version.appVersion}</p><p className="text-xs text-muted-foreground">{version.environment} · {version.gitCommit}</p></CardContent></Card>}
        {health && <Card><CardContent className="py-3"><p className="text-xs text-muted-foreground">Status</p><Badge className={S[health.overallStatus]}>{health.overallStatus}</Badge></CardContent></Card>}
        {errors && <Card><CardContent className="py-3"><p className="text-xs text-muted-foreground">Errors (24h)</p><p className="text-2xl font-bold text-red-600">{errors.errorsLast24h}</p></CardContent></Card>}
        {errors && <Card><CardContent className="py-3"><p className="text-xs text-muted-foreground">Total Issues</p><p className="text-2xl font-bold">{errors.totalIssues}</p></CardContent></Card>}
      </div>
      {health?.components && (
        <Card><CardHeader className="pb-2"><CardTitle className="text-base">Components</CardTitle></CardHeader><CardContent className="space-y-2">
          {health.components.map(c => (
            <div key={c.component} className="flex justify-between items-center text-sm">
              <div className="flex items-center gap-2">
                {c.status === 'OK' ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : c.status === 'WARNING' ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                <span className="font-medium">{c.component}</span>
              </div>
              <span className="text-xs text-muted-foreground">{c.message?.substring(0, 50)}</span>
            </div>
          ))}
        </CardContent></Card>
      )}
      <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
    </div>
  );
}

function BackupsTab() {
  const [backups, setBackups] = useState([]);
  const [running, setRunning] = useState('');
  const [validation, setValidation] = useState(null);
  const load = () => api.get('/system/backups?pageSize=30').then(r => setBackups(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const run = async (type) => {
    setRunning(type);
    try {
      const r = await api.post(`/system/backups/${type}`);
      const d = r.data.data;
      toast.success(`${type} backup: ${d.status} (${((d.fileSize || 0) / 1024).toFixed(0)} KB)`);
      load();
    } catch { toast.error('Backup failed'); }
    setRunning('');
  };

  const validate = async (id) => {
    try {
      const r = await api.post(`/system/backups/${id}/validate-restore`);
      setValidation(r.data.data);
      toast[r.data.data.valid ? 'success' : 'error'](`Restore validation: ${r.data.data.valid ? 'VALID' : 'INVALID'}`);
    } catch { toast.error('Validation failed'); }
  };

  // Stats
  const totalBackups = backups.length;
  const completed = backups.filter(b => b.status === 'COMPLETED').length;
  const failed = backups.filter(b => b.status === 'FAILED').length;
  const totalSize = backups.reduce((sum, b) => sum + (b.fileSize || 0), 0);
  const lastBackup = backups.length > 0 ? backups[0] : null;

  return (
    <div className="space-y-4" data-testid="backups-section">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold">{totalBackups}</p><p className="text-xs text-muted-foreground">Total Backups</p></CardContent></Card>
        <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold text-green-600">{completed}</p><p className="text-xs text-muted-foreground">Completed</p></CardContent></Card>
        <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold text-red-600">{failed}</p><p className="text-xs text-muted-foreground">Failed</p></CardContent></Card>
        <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold">{(totalSize / 1024 / 1024).toFixed(1)} MB</p><p className="text-xs text-muted-foreground">Total Size</p></CardContent></Card>
        <Card><CardContent className="py-3 text-center"><p className="text-xs text-muted-foreground">Last Backup</p><p className="text-sm font-medium">{lastBackup ? lastBackup.startedAt?.slice(0, 16).replace('T', ' ') : 'Never'}</p></CardContent></Card>
      </div>

      {/* Actions */}
      <div className="flex gap-2 flex-wrap">
        <Button onClick={() => run('database')} disabled={!!running} data-testid="backup-db">
          <Database className="w-4 h-4 mr-2" />{running === 'database' ? 'Running...' : 'Database Backup'}
        </Button>
        <Button onClick={() => run('files')} disabled={!!running} data-testid="backup-files">
          <FileText className="w-4 h-4 mr-2" />{running === 'files' ? 'Running...' : 'Files Backup'}
        </Button>
        <Button variant="default" onClick={() => run('full')} disabled={!!running} data-testid="backup-full">
          {running === 'full' ? 'Running...' : 'Full Backup'}
        </Button>
        <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
      </div>

      {/* Validation result */}
      {validation && (
        <Card className={validation.valid ? 'border-green-200' : 'border-red-200'}>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2">
            {validation.valid ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
            Restore Validation: {validation.valid ? 'VALID' : 'INVALID'}
          </CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <p>Type: {validation.backupType} | Status: {validation.status}</p>
            {validation.estimatedContents?.map((c, i) => (
              <p key={i} className="text-xs text-muted-foreground">{c.file}: {c.type} ({((c.size || 0) / 1024).toFixed(0)} KB) {c.entries ? `· ${c.entries} entries` : ''}</p>
            ))}
            {validation.warnings?.length > 0 && (
              <div className="mt-2">{validation.warnings.map((w, i) => <p key={i} className="text-xs text-amber-600">{w}</p>)}</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Backup list */}
      <div className="space-y-2">
        {backups.map(b => (
          <Card key={b.id}>
            <CardContent className="flex justify-between items-center py-3">
              <div className="flex items-center gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{b.backupType}</Badge>
                    <Badge className={b.status === 'COMPLETED' ? 'bg-green-500 text-white' : b.status === 'FAILED' ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'}>{b.status}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 flex gap-3">
                    <span>{b.startedAt?.slice(0, 19).replace('T', ' ')}</span>
                    <span>{((b.fileSize || 0) / 1024).toFixed(0)} KB</span>
                    {b.filePath && <span className="font-mono truncate max-w-[200px]">{b.filePath.split('/').pop()}</span>}
                  </div>
                  {b.errorMessage && <p className="text-xs text-red-500 mt-1">{b.errorMessage}</p>}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="outline" onClick={() => validate(b.id)} data-testid={`validate-${b.id}`}>Validate</Button>
                {b.status === 'COMPLETED' && b.filePath && (
                  <Button size="sm" variant="ghost" data-testid={`download-${b.id}`} onClick={() => toast.success('Backup artifact available at server path: ' + b.filePath)}>
                    <Download className="w-3 h-3 mr-1" />Path
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        {backups.length === 0 && <p className="text-center text-muted-foreground py-6">No backups yet — run your first backup above</p>}
      </div>
    </div>
  );
}

function LogsTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [level, setLevel] = useState('all');
  const [component, setComponent] = useState('all');
  const [requestId, setRequestId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    const p = new URLSearchParams({ page: String(page), pageSize: '30' });
    if (level !== 'all') p.set('level', level);
    if (component !== 'all') p.set('component', component);
    if (requestId.trim()) p.set('requestId', requestId.trim());
    if (dateFrom) p.set('dateFrom', dateFrom);
    if (dateTo) p.set('dateTo', dateTo);
    try { const r = await api.get(`/system/logs?${p}`); setLogs(r.data.items || []); setTotal(r.data.total || 0); } catch {}
  }, [page, level, component, requestId, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    try {
      const p = new URLSearchParams({ format: 'CSV', pageSize: '5000' });
      if (level !== 'all') p.set('level', level);
      if (component !== 'all') p.set('component', component);
      if (dateFrom) p.set('dateFrom', dateFrom);
      if (dateTo) p.set('dateTo', dateTo);
      // Use audit export as fallback since system logs don't have their own export yet
      const res = await api.get(`/security/audit/export?${p}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'system_logs_export.csv'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Logs exported');
    } catch { toast.error('Export failed'); }
  };

  const viewDetail = async (id) => {
    try {
      const r = await api.get(`/system/logs/${id}`);
      setDetail(r.data.data);
    } catch { toast.error('Failed to load log'); }
  };

  // Stats
  const errorCount = logs.filter(l => l.level === 'ERROR' || l.level === 'CRITICAL').length;
  const warnCount = logs.filter(l => l.level === 'WARNING').length;

  return (
    <div className="space-y-4" data-testid="logs-section">
      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-end">
        <Select value={level} onValueChange={v => { setLevel(v); setPage(1); }}>
          <SelectTrigger className="w-32" data-testid="log-level-filter"><SelectValue placeholder="Level" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Levels</SelectItem>{['INFO', 'WARNING', 'ERROR', 'CRITICAL'].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={component} onValueChange={v => { setComponent(v); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="log-component-filter"><SelectValue placeholder="Component" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Components</SelectItem>{['API', 'AUTH', 'DATABASE', 'ROSTER', 'COMMUNICATIONS', 'WEBHOOKS', 'SYNC', 'ACCOUNTING', 'ENGAGEMENT', 'SECURITY', 'BACKUP', 'SYSTEM'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <div><Input placeholder="Request ID" value={requestId} onChange={e => { setRequestId(e.target.value); setPage(1); }} className="w-32 h-9" data-testid="log-requestid" /></div>
        <div><Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} className="w-36 h-9" data-testid="log-date-from" /></div>
        <div><Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} className="w-36 h-9" data-testid="log-date-to" /></div>
        <Button size="sm" variant="outline" onClick={handleExport} data-testid="export-logs-btn"><Download className="w-3 h-3 mr-1" />CSV</Button>
        <span className="text-sm text-muted-foreground">{total} logs {errorCount > 0 && <span className="text-red-500">({errorCount} errors)</span>} {warnCount > 0 && <span className="text-amber-500">({warnCount} warnings)</span>}</span>
      </div>

      {/* Log entries */}
      <div className="space-y-0.5">
        {logs.map(l => (
          <div key={l.id} className="flex items-start gap-2 text-sm py-1.5 border-b border-dashed cursor-pointer hover:bg-muted/40" onClick={() => viewDetail(l.id)} data-testid={`log-row-${l.id}`}>
            <Badge className={`${LL[l.level] || ''} text-[10px] shrink-0 mt-0.5`}>{l.level}</Badge>
            <span className="text-xs text-muted-foreground font-mono w-16 shrink-0">{l.component}</span>
            <span className="text-muted-foreground flex-1 truncate">{l.message}</span>
            {l.requestId && <span className="text-[10px] text-muted-foreground font-mono shrink-0">{l.requestId}</span>}
            <span className="text-[10px] text-muted-foreground shrink-0">{l.timestamp?.slice(11, 19)}</span>
          </div>
        ))}
        {logs.length === 0 && <p className="text-center text-muted-foreground py-4">No logs found</p>}
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center">
        <span className="text-xs text-muted-foreground">Page {page}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" disabled={logs.length < 30} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Detail Dialog */}
      {detail && (
        <Card className="border-2">
          <CardHeader className="pb-2 flex flex-row justify-between items-start">
            <CardTitle className="text-sm flex items-center gap-2">
              <Badge className={LL[detail.level] || ''}>{detail.level}</Badge>
              {detail.component}
            </CardTitle>
            <Button size="sm" variant="ghost" onClick={() => setDetail(null)}>Close</Button>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p><span className="text-muted-foreground">Time:</span> {detail.timestamp}</p>
            <p><span className="text-muted-foreground">Request ID:</span> <span className="font-mono">{detail.requestId || '—'}</span></p>
            <p><span className="text-muted-foreground">User ID:</span> <span className="font-mono">{detail.userId || '—'}</span></p>
            <p><span className="text-muted-foreground">Branch:</span> {detail.branchId || '—'}</p>
            <div><span className="text-muted-foreground">Message:</span><p className="mt-1 p-2 bg-muted rounded text-xs whitespace-pre-wrap">{detail.message}</p></div>
            {detail.context && Object.keys(detail.context).length > 0 && (
              <div><span className="text-muted-foreground">Context:</span><pre className="mt-1 p-2 bg-muted rounded text-xs overflow-auto max-h-40">{JSON.stringify(detail.context, null, 2)}</pre></div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function AdminToolsTab() {
  const [running, setRunning] = useState('');
  const [results, setResults] = useState([]);

  const addResult = (label, data, success) => {
    setResults(prev => [{ label, data, success, timestamp: new Date().toLocaleTimeString() }, ...prev].slice(0, 10));
  };

  const recalc = async (targets, label) => {
    setRunning(label);
    try {
      const r = await api.post('/system/recalculate', { targets });
      addResult(label, r.data.data.results, true);
      toast.success(`${label}: complete`);
    } catch { addResult(label, { error: 'Failed' }, false); toast.error(`${label} failed`); }
    setRunning('');
  };

  const retryComms = async () => {
    setRunning('Retry Comms');
    try {
      const r = await api.post('/system/retry-failed-communications');
      const d = r.data.data;
      addResult('Retry Communications', { sms: `${d.sms.succeeded}/${d.sms.totalFailed} succeeded`, email: `${d.email.succeeded}/${d.email.totalFailed} succeeded` }, true);
      toast.success(`Retried: SMS ${d.sms.retried}, Email ${d.email.retried}`);
    } catch { addResult('Retry Communications', { error: 'Failed' }, false); toast.error('Retry failed'); }
    setRunning('');
  };

  const retryWebhooks = async () => {
    setRunning('Retry Webhooks');
    try {
      const r = await api.post('/system/retry-failed-webhooks');
      const d = r.data.data;
      addResult('Retry Webhooks', { succeeded: d.succeeded, total: d.totalFailed, retried: d.retried }, true);
      toast.success(`Retried: ${d.retried}/${d.totalFailed}, ${d.succeeded} succeeded`);
    } catch { addResult('Retry Webhooks', { error: 'Failed' }, false); toast.error('Retry failed'); }
    setRunning('');
  };

  const refreshCaches = async () => {
    setRunning('Refresh Caches');
    try {
      const r = await api.post('/system/recalculate', { targets: ['dashboard_caches'] });
      addResult('Refresh Caches', r.data.data.results, true);
      toast.success('Caches refreshed');
    } catch { addResult('Refresh Caches', { error: 'Failed' }, false); toast.error('Failed'); }
    setRunning('');
  };

  const indexStatus = async () => {
    setRunning('Index Status');
    try {
      const r = await api.get('/system/index-status');
      const d = r.data.data;
      addResult('Index Status', { collections: d.totalCollections, indexes: d.totalIndexes }, true);
      toast.success(`${d.totalCollections} collections, ${d.totalIndexes} indexes`);
    } catch { addResult('Index Status', { error: 'Failed' }, false); }
    setRunning('');
  };

  return (
    <div className="space-y-4" data-testid="admin-tools-section">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Button variant="outline" className="h-auto py-5 flex-col gap-2" onClick={() => recalc(['funding_forecasts'], 'Recalculate Forecasts')} disabled={!!running} data-testid="recalc-forecasts-btn">
          <RefreshCw className={`w-5 h-5 ${running === 'Recalculate Forecasts' ? 'animate-spin' : ''}`} />
          <span className="text-xs font-medium">Recalculate Forecasts</span>
        </Button>
        <Button variant="outline" className="h-auto py-5 flex-col gap-2" onClick={() => recalc(['risk_scores', 'recommendations'], 'Recalculate Risk Scores')} disabled={!!running} data-testid="recalc-risk-btn">
          <RefreshCw className={`w-5 h-5 ${running === 'Recalculate Risk Scores' ? 'animate-spin' : ''}`} />
          <span className="text-xs font-medium">Recalculate Risk Scores</span>
        </Button>
        <Button variant="outline" className="h-auto py-5 flex-col gap-2" onClick={retryComms} disabled={!!running} data-testid="retry-comms-btn">
          <Play className={`w-5 h-5 ${running === 'Retry Comms' ? 'animate-pulse' : ''}`} />
          <span className="text-xs font-medium">Retry Failed Communications</span>
        </Button>
        <Button variant="outline" className="h-auto py-5 flex-col gap-2" onClick={retryWebhooks} disabled={!!running} data-testid="retry-webhooks-btn">
          <Play className={`w-5 h-5 ${running === 'Retry Webhooks' ? 'animate-pulse' : ''}`} />
          <span className="text-xs font-medium">Retry Failed Webhooks</span>
        </Button>
        <Button variant="outline" className="h-auto py-5 flex-col gap-2" onClick={refreshCaches} disabled={!!running} data-testid="refresh-caches-btn">
          <RefreshCw className={`w-5 h-5 ${running === 'Refresh Caches' ? 'animate-spin' : ''}`} />
          <span className="text-xs font-medium">Refresh Dashboard Caches</span>
        </Button>
        <Button variant="outline" className="h-auto py-5 flex-col gap-2" onClick={indexStatus} disabled={!!running} data-testid="index-status-btn">
          <Database className="w-5 h-5" />
          <span className="text-xs font-medium">Index Status</span>
        </Button>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Action Results</p>
          {results.map((r, i) => (
            <Card key={i} className={r.success ? 'border-green-200' : 'border-red-200'}>
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {r.success ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                    <span className="font-medium text-sm">{r.label}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{r.timestamp}</span>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  {typeof r.data === 'object' && Object.entries(r.data).map(([k, v]) => (
                    <span key={k} className="mr-3">{k}: <span className="font-medium text-foreground">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span></span>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ApiDocsTab() {
  const [catalog, setCatalog] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  useEffect(() => { api.get('/system/api-catalog').then(r => setCatalog(r.data.data)).catch(() => {}); }, []);
  if (!catalog) return <p className="text-muted-foreground py-4">Loading...</p>;

  const classifyRoute = (m) => {
    if (m.authRequired === false) return 'PUBLIC';
    if (m.portalAuth) return 'PORTAL';
    return 'INTERNAL';
  };
  const ROUTE_BADGE = { INTERNAL: 'bg-blue-100 text-blue-700', PORTAL: 'bg-purple-100 text-purple-700', PUBLIC: 'bg-green-100 text-green-700' };
  const METHOD_COLORS = { GET: 'bg-green-100 text-green-700 border-green-200', POST: 'bg-blue-100 text-blue-700 border-blue-200', PUT: 'bg-amber-100 text-amber-700 border-amber-200', DELETE: 'bg-red-100 text-red-700 border-red-200' };

  const filtered = catalog.modules.filter(m => {
    if (typeFilter !== 'all' && classifyRoute(m) !== typeFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return m.module.toLowerCase().includes(q) || m.endpoints.some(e => e.path.toLowerCase().includes(q) || e.description.toLowerCase().includes(q));
  });

  const totalFiltered = filtered.reduce((sum, m) => sum + m.endpoints.filter(e => !search || e.path.toLowerCase().includes(search.toLowerCase()) || e.description.toLowerCase().includes(search.toLowerCase())).length, 0);

  return (
    <div className="space-y-4" data-testid="api-docs-section">
      {/* Header */}
      <div className="flex flex-wrap gap-3 items-center">
        <Card><CardContent className="py-2 px-4 text-center"><p className="text-xl font-bold">{catalog.totalModules}</p><p className="text-[10px] text-muted-foreground">Modules</p></CardContent></Card>
        <Card><CardContent className="py-2 px-4 text-center"><p className="text-xl font-bold">{catalog.totalEndpoints}</p><p className="text-[10px] text-muted-foreground">Endpoints</p></CardContent></Card>
        <a href="/docs" target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm" data-testid="swagger-link"><BookOpen className="w-4 h-4 mr-1" />Swagger UI</Button></a>
        <a href="/redoc" target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm">ReDoc</Button></a>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Input placeholder="Search endpoints..." value={search} onChange={e => setSearch(e.target.value)} className="pl-3" data-testid="api-search" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-36" data-testid="api-type-filter"><SelectValue placeholder="Route Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Routes</SelectItem>
            <SelectItem value="INTERNAL">Internal</SelectItem>
            <SelectItem value="PORTAL">Portal</SelectItem>
            <SelectItem value="PUBLIC">Public</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">{totalFiltered} endpoints</span>
      </div>

      {/* Module cards */}
      {filtered.map(m => {
        const routeType = classifyRoute(m);
        const endpoints = search
          ? m.endpoints.filter(e => e.path.toLowerCase().includes(search.toLowerCase()) || e.description.toLowerCase().includes(search.toLowerCase()))
          : m.endpoints;
        if (endpoints.length === 0) return null;
        return (
          <Card key={m.module}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                {m.module}
                <Badge className={ROUTE_BADGE[routeType]}>{routeType}</Badge>
                <span className="text-xs text-muted-foreground font-normal">{endpoints.length} endpoints</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {endpoints.map((e, i) => (
                <div key={i} className="flex items-center gap-2 text-xs py-0.5">
                  <Badge className={`${METHOD_COLORS[e.method] || ''} font-mono w-14 justify-center text-[10px]`}>{e.method}</Badge>
                  <code className="text-muted-foreground font-mono flex-1">{e.path}</code>
                  {e.auth === false && <Badge variant="outline" className="text-[10px] bg-green-50">No Auth</Badge>}
                  <span className="text-muted-foreground ml-auto">{e.description}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })}
      {filtered.length === 0 && <p className="text-center text-muted-foreground py-6">No endpoints match your search</p>}
    </div>
  );
}

function ChecklistTab() {
  const [data, setData] = useState(null);
  const [catFilter, setCatFilter] = useState('all');
  const load = () => {
    const p = catFilter !== 'all' ? `?category=${catFilter}` : '';
    api.get(`/system/launch-checklist${p}`).then(r => setData(r.data.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [catFilter]);
  const update = async (id, status) => {
    try { await api.put(`/system/launch-checklist/${id}`, { status }); toast.success('Updated'); load(); } catch { toast.error('Failed'); }
  };
  if (!data) return <p className="text-muted-foreground py-4">Loading...</p>;
  const s = data.summary;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-3">
        <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold">{s.total}</p><p className="text-xs text-muted-foreground">Total</p></CardContent></Card>
        <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold text-green-600">{s.done}</p><p className="text-xs text-muted-foreground">Done</p></CardContent></Card>
        <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold text-slate-500">{s.pending}</p><p className="text-xs text-muted-foreground">Pending</p></CardContent></Card>
        <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold">{s.completionPercent}%</p><p className="text-xs text-muted-foreground">Complete</p></CardContent></Card>
      </div>
      <div className="h-3 bg-muted rounded-full overflow-hidden"><div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${s.completionPercent}%` }} /></div>
      <Select value={catFilter} onValueChange={setCatFilter}>
        <SelectTrigger className="w-48"><SelectValue placeholder="Category" /></SelectTrigger>
        <SelectContent><SelectItem value="all">All Categories</SelectItem>{data.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
      </Select>
      <div className="space-y-1">
        {data.items.map(item => (
          <div key={item.id} className="flex items-center justify-between py-2 border-b border-dashed text-sm">
            <div className="flex items-center gap-2 min-w-0">
              <Badge className={`${LS[item.status]} text-[10px] shrink-0`}>{item.status}</Badge>
              <Badge variant="outline" className="text-[10px] shrink-0">{item.category}</Badge>
              <span className={item.status === 'DONE' ? 'line-through text-muted-foreground' : ''}>{item.item}</span>
            </div>
            <div className="flex gap-1 shrink-0">
              {item.status !== 'DONE' && <Button size="sm" variant="ghost" className="text-green-600 h-7 px-2" onClick={() => update(item.id, 'DONE')}>Done</Button>}
              {item.status === 'PENDING' && <Button size="sm" variant="ghost" className="text-red-500 h-7 px-2" onClick={() => update(item.id, 'BLOCKED')}>Block</Button>}
              {item.status !== 'PENDING' && <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => update(item.id, 'PENDING')}>Reset</Button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SystemAdminPage() {
  return (
    <div className="space-y-6" data-testid="system-admin-page">
      <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Wrench className="w-6 h-6" />System Administration</h1>
      <Tabs defaultValue="health">
        <TabsList className="flex-wrap">
          <TabsTrigger value="health" data-testid="tab-health"><Activity className="w-4 h-4 mr-1" />Health</TabsTrigger>
          <TabsTrigger value="backups" data-testid="tab-backups"><Database className="w-4 h-4 mr-1" />Backups</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs"><FileText className="w-4 h-4 mr-1" />Logs</TabsTrigger>
          <TabsTrigger value="tools" data-testid="tab-tools"><Wrench className="w-4 h-4 mr-1" />Admin Tools</TabsTrigger>
          <TabsTrigger value="api" data-testid="tab-api"><BookOpen className="w-4 h-4 mr-1" />API Docs</TabsTrigger>
          <TabsTrigger value="checklist" data-testid="tab-checklist"><CheckSquare className="w-4 h-4 mr-1" />Launch Checklist</TabsTrigger>
        </TabsList>
        <TabsContent value="health" className="mt-4"><HealthTab /></TabsContent>
        <TabsContent value="backups" className="mt-4"><BackupsTab /></TabsContent>
        <TabsContent value="logs" className="mt-4"><LogsTab /></TabsContent>
        <TabsContent value="tools" className="mt-4"><AdminToolsTab /></TabsContent>
        <TabsContent value="api" className="mt-4"><ApiDocsTab /></TabsContent>
        <TabsContent value="checklist" className="mt-4"><ChecklistTab /></TabsContent>
      </Tabs>
    </div>
  );
}
